// src/modules/settings/RemoteAccessSettings.tsx
// Remote Access (Splashtop) block of the tenant settings page: the edit fields (inside the page's Formik form, under
// the "remoteAccess" value) and the read-only summary.
import { ErrorMessage, Field, useFormikContext } from 'formik';
import { AlertCircle } from 'lucide-react';
import {
  CLIENT_TYPE_OPTIONS,
  NOTIFICATION_MODE_OPTIONS,
  REQUEST_PERMISSION_OPTIONS,
  RemoteAccessFormValues,
  TenantGroupOption,
  clientTypeLabel,
  notificationModeLabel,
  permissionLabel,
  requestPermissionLabel,
} from './helpers/remoteAccessSettings';

const inputClass =
  'w-full bg-[#0f0f0f] border border-gray-600 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all hover:border-gray-500';

const ToggleSwitch = ({ checked, label, onToggle }: { checked: boolean; label: string; onToggle: () => void }) => (
  <div
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-red-600' : 'bg-gray-600'}`}
    onClick={onToggle}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`}
    />
  </div>
);

const SwitchRow = ({ name, label, description }: { name: string; label: string; description?: string }) => {
  const { getFieldProps, setFieldValue } = useFormikContext<any>();
  const checked = !!getFieldProps(name).value;
  return (
    <div className="flex items-center justify-between gap-4 p-4 bg-[#0f0f0f] border border-gray-600 rounded-lg hover:border-gray-500 transition-all">
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-300">{label}</span>
        {description && <span className="text-xs text-gray-500 mt-1">{description}</span>}
      </div>
      <ToggleSwitch checked={checked} label={label} onToggle={() => setFieldValue(name, !checked)} />
    </div>
  );
};

const FieldError = ({ name }: { name: string }) => (
  <ErrorMessage name={name}>
    {(msg) =>
      typeof msg === 'string' ? (
        <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
          <AlertCircle size={12} />
          {msg}
        </p>
      ) : null
    }
  </ErrorMessage>
);

const SubHeading = ({ title, description }: { title: string; description: string }) => (
  <div className="mt-6 mb-3">
    <h4 className="text-base font-semibold text-white">{title}</h4>
    <p className="text-xs text-gray-500 mt-1">{description}</p>
  </div>
);

interface RemoteAccessEditFieldsProps {
  /** null when the groups couldn't be loaded. */
  tenantGroups: TenantGroupOption[] | null;
}

export const RemoteAccessEditFields = ({ tenantGroups }: RemoteAccessEditFieldsProps) => {
  const { values, setFieldValue } = useFormikContext<{ remoteAccess: RemoteAccessFormValues }>();
  const ra = values.remoteAccess;
  const selectedGroups = new Set(ra.groupIds.map((id) => id.toLowerCase()));
  const knownGroupIds = new Set((tenantGroups ?? []).map((g) => g.id.toLowerCase()));
  // Groups still in the setting but no longer in the tenant (deleted); listed so they can be removed.
  const unknownGroupIds = ra.groupIds.filter((id) => !knownGroupIds.has(id.toLowerCase()));

  const toggleGroup = (id: string) => {
    const next = selectedGroups.has(id.toLowerCase())
      ? ra.groupIds.filter((g) => g.toLowerCase() !== id.toLowerCase())
      : [...ra.groupIds, id];
    setFieldValue('remoteAccess.groupIds', next);
  };

  const requestPermissionHelp = REQUEST_PERMISSION_OPTIONS.find((o) => o.value === Number(ra.requestPermission))?.description;
  const clientTypeHelp = CLIENT_TYPE_OPTIONS.find((o) => o.value === ra.clientType)?.description;

  return (
    <div className="space-y-3">
      <SwitchRow
        name="remoteAccess.enabled"
        label="Enable Remote Access (Splashtop)"
        description="Lets admins open a remote session to this tenant's devices. Devices set it up at their next agent check-in."
      />

      <SubHeading
        title="Devices"
        description="Applied by each device at its next agent check-in (within a few hours, or when the agent restarts)."
      />
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-2">User approval</label>
        <Field
          as="select"
          name="remoteAccess.requestPermission"
          className={inputClass}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFieldValue('remoteAccess.requestPermission', Number(e.target.value))}
        >
          {REQUEST_PERMISSION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Field>
        {requestPermissionHelp && <p className="text-xs text-gray-500 mt-1">{requestPermissionHelp}</p>}
      </div>
      <SwitchRow
        name="remoteAccess.hideTrayIcon"
        label="Hide the Splashtop icon"
        description="Hides the streamer's icon in the device's system tray."
      />
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-2">Idle session timeout (minutes)</label>
        <Field type="number" min="0" name="remoteAccess.idleTimeoutMinutes" className={inputClass} />
        <FieldError name="remoteAccess.idleTimeoutMinutes" />
        <p className="text-xs text-gray-500 mt-1">Disconnects a session with no activity after this many minutes. 0 = never.</p>
      </div>
      <SwitchRow
        name="remoteAccess.limitToGroups"
        label="Only some groups"
        description="Off: every device in the tenant. On: only devices in the groups picked below."
      />
      {ra.limitToGroups && (
        <div className="p-4 bg-[#0f0f0f] border border-gray-600 rounded-lg">
          {tenantGroups === null && (
            <p className="text-xs text-red-400 mb-2 flex items-center gap-1">
              <AlertCircle size={12} />
              The group list couldn&apos;t be loaded. Groups already picked are kept.
            </p>
          )}
          {tenantGroups !== null && tenantGroups.length === 0 && <p className="text-sm text-gray-400">This tenant has no groups.</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(tenantGroups ?? []).map((g) => (
              <label key={g.id} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-red-600"
                  checked={selectedGroups.has(g.id.toLowerCase())}
                  onChange={() => toggleGroup(g.id)}
                />
                {g.name}
              </label>
            ))}
            {unknownGroupIds.map((id) => (
              <label key={id} className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
                <input type="checkbox" className="accent-red-600" checked onChange={() => toggleGroup(id)} />
                {tenantGroups === null ? id : `Unknown group (${id})`}
              </label>
            ))}
          </div>
          <FieldError name="remoteAccess.groupIds" />
        </div>
      )}

      <SubHeading title="Sessions" description="Applied to the next session that is started." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Open sessions in</label>
          <Field as="select" name="remoteAccess.clientType" className={inputClass}>
            {CLIENT_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Field>
          {clientTypeHelp && <p className="text-xs text-gray-500 mt-1">{clientTypeHelp}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Tell the user a session is active with</label>
          <Field
            as="select"
            name="remoteAccess.notificationMode"
            className={inputClass}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFieldValue('remoteAccess.notificationMode', Number(e.target.value))}
          >
            {NOTIFICATION_MODE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Field>
        </div>
      </div>
      <SwitchRow
        name="remoteAccess.allowCloseBanner"
        label="Let the user close the banner"
        description="When off, the session banner stays on screen."
      />

      <SubHeading title="Allowed in a session" description="What an admin can do during a remote session." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.keys(ra.permissions).map((name) => (
          <SwitchRow key={name} name={`remoteAccess.permissions.${name}`} label={permissionLabel(name)} />
        ))}
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => (
  <div className="group relative flex items-center justify-between gap-4 py-4 px-5 bg-gradient-to-r from-[#0f0f0f] to-[#1a1a1a] rounded-lg border border-gray-700/50 hover:border-red-500/40 transition-all duration-300">
    <span className="text-gray-400 text-sm font-medium group-hover:text-gray-200 transition-colors">{label}</span>
    <span
      className={`px-3 py-1 rounded-md border font-semibold text-sm text-right ${
        highlight === false ? 'bg-red-900/30 text-red-400 border-red-500/50' : 'bg-[#2a2a2a] text-white border-gray-600/50'
      }`}
    >
      {value}
    </span>
  </div>
);

interface RemoteAccessSummaryProps {
  /** null when the setting couldn't be loaded. */
  settings: RemoteAccessFormValues | null;
  tenantGroups: TenantGroupOption[] | null;
}

export const RemoteAccessSummary = ({ settings, tenantGroups }: RemoteAccessSummaryProps) => {
  if (!settings) {
    return <SummaryRow label="Remote Access (Splashtop)" value="Unavailable" highlight={false} />;
  }

  const groupNames = settings.groupIds.map(
    (id) => tenantGroups?.find((g) => g.id.toLowerCase() === id.toLowerCase())?.name ?? (tenantGroups ? `Unknown group (${id})` : id),
  );
  const allowed = Object.entries(settings.permissions)
    .filter(([, on]) => on)
    .map(([name]) => permissionLabel(name));

  return (
    <div className="space-y-3">
      <SummaryRow label="Remote Access (Splashtop)" value={settings.enabled ? 'Enabled' : 'Disabled'} highlight={settings.enabled} />
      {settings.enabled && (
        <>
          <SummaryRow label="User approval" value={requestPermissionLabel(settings.requestPermission)} />
          <SummaryRow label="Devices" value={settings.limitToGroups ? groupNames.join(', ') : 'All groups'} />
          <SummaryRow label="Splashtop icon" value={settings.hideTrayIcon ? 'Hidden' : 'Shown'} />
          <SummaryRow
            label="Idle session timeout"
            value={Number(settings.idleTimeoutMinutes) > 0 ? `${settings.idleTimeoutMinutes} minutes` : 'Never'}
          />
          <SummaryRow label="Open sessions in" value={clientTypeLabel(settings.clientType)} />
          <SummaryRow
            label="Session notice"
            value={`${notificationModeLabel(settings.notificationMode)}${settings.allowCloseBanner ? ' (banner can be closed)' : ''}`}
          />
          <SummaryRow label="Allowed in a session" value={allowed.length > 0 ? allowed.join(', ') : 'Screen only'} />
        </>
      )}
    </div>
  );
};
